/**
 * Shared resilience constants and logging for salon flow.
 */

export const TIMEOUT_MS = {
  DEFAULT: 20000,
  /** Align with server MAX_LOCK_HOLD_SECONDS (~195s) + ONNX headroom for batch detect. */
  BATCH_DETECT: 240000,
  SINGLE_DETECT: 210000,
  REPORT: 30000,
  SAVE_ANALYSIS: 45000,
  FLOW_STAGE: 6000,
};

export const RETRY = {
  MAX_ATTEMPTS: 3,
  /** Long-poll retries when another detect holds the Redis lock (409). */
  CONFLICT_MAX_ATTEMPTS: 32,
  CONFLICT_BASE_DELAY_MS: 5000,
  BASE_DELAY_MS: 1000,
  MAX_DELAY_MS: 8000,
  JITTER_MS: 250,
};

export const REQUEST_STATUS = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  RETRYING: 'retrying',
  QUEUED: 'queued',
  TIMEOUT: 'timeout',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed',
  RETRY_AVAILABLE: 'retry_available',
};

const RETRYABLE_HTTP = new Set([502, 503, 504]);

export function logSalonResilience(event, details = {}) {
  const payload = { event, ts: new Date().toISOString(), ...details };
  if (event === 'retry_exhausted' || event === 'timeout' || event === 'failed') {
    console.warn('[salon-resilience]', payload);
  } else {
    console.info('[salon-resilience]', payload);
  }
}

export function isAbortError(err) {
  return err?.name === 'AbortError' || err?.code === 'ABORT_ERR';
}

export function isTimeoutError(err) {
  return err?.name === 'AbortError' && err?.reason === 'timeout';
}

export function isRetryableHttpStatus(status) {
  return RETRYABLE_HTTP.has(Number(status));
}

export function isProcessingConflictPayload(data) {
  if (!data) return false;
  return data.code === 'PROCESSING_IN_PROGRESS';
}

export function isRetryableApiPayload(data) {
  if (!data) return false;
  if (data.retryable === true) return true;
  return isProcessingConflictPayload(data);
}

export function conflictRetryDelayMs(data, attempt = 0) {
  const fromApi = Number(data?.retry_after_seconds);
  if (Number.isFinite(fromApi) && fromApi > 0) {
    return Math.min(fromApi * 1000, 15000);
  }
  return Math.min(RETRY.CONFLICT_BASE_DELAY_MS + attempt * 500, 10000);
}

export function isRetryableError(err, data = null) {
  if (isAbortError(err) && !isTimeoutError(err)) return false;
  if (isTimeoutError(err)) return true;
  if (err?.retryable === true) return true;
  if (isRetryableHttpStatus(err?.status)) return true;
  if (isRetryableApiPayload(data)) return true;
  if (err?.name === 'TypeError') return true;
  if (err?.message && /failed to fetch|network|load failed/i.test(err.message)) return true;
  return false;
}

export function computeBackoffMs(attempt, baseMs = RETRY.BASE_DELAY_MS, maxMs = RETRY.MAX_DELAY_MS) {
  const exp = Math.min(maxMs, baseMs * 2 ** attempt);
  const jitter = Math.floor(Math.random() * RETRY.JITTER_MS);
  return exp + jitter;
}

export function sleep(ms, signal) {
  if (!signal) {
    return new Promise((resolve) => { window.setTimeout(resolve, ms); });
  }
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export function createClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `salon-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function createRequestScope() {
  const controller = new AbortController();
  const requestId = createClientRequestId();
  return {
    requestId,
    signal: controller.signal,
    abort: (reason) => controller.abort(reason),
    isAborted: () => controller.signal.aborted,
  };
}
